<script setup lang="ts">
import type { IntervalInputProps } from '.';

import { cn } from '@/utils';
import { useId } from 'vue';

import IconArrowLeftRight from '~icons/mdi/arrow-left-right';

import CurrencyInput from '../CurrencyInput/Input.vue';
import {
	intervalInputClassesVariants,
	intervalInputIconVariants,
	intervalInputLabelVariants,
	intervalInputSuffixVariants,
	intervalInputTitleVariants
} from './variants';

const props = withDefaults(defineProps<IntervalInputProps>(), {
	updateOn: 'input',
	variant: 'primary'
});

const firstInputId = useId();
const secondInputId = useId();

const localMin = ref<number | null | undefined>(undefined);
const localMax = ref<number | null | undefined>(undefined);

const min = defineModel<number | null | undefined>('min', {
	default: () => null
});
const max = defineModel<number | null | undefined>('max', {
	default: () => null
});

const unit = computed(() => (props.unit === 'area' ? 'm²' : 'R$'));

const unitPosition = computed(() => (props.unit === 'area' ? 'right' : 'left'));

function validateAndUpdate(key: 'min' | 'max') {
	const minVal = localMin.value;
	const maxVal = localMax.value;

	if (minVal == null || maxVal == null) return;

	if (key === 'min') {
		if (minVal > maxVal) {
			localMin.value = null;
		}
	}

	if (key === 'max') {
		if (maxVal < minVal) {
			localMax.value = null;
		}
	}
}

function handleInput(key: 'min' | 'max') {
	if (props.updateOn !== 'input') return;

	validateAndUpdate(key);

	if (key === 'min') {
		min.value = localMin.value;
	}

	if (key === 'max') {
		max.value = localMax.value;
	}
}

function handleBlur() {
	if (min.value !== localMin.value) {
		validateAndUpdate('min');
		min.value = localMin.value;
	}

	if (max.value !== localMax.value) {
		validateAndUpdate('max');
		max.value = localMax.value;
	}
}

watch(
	min,
	newMin => {
		localMin.value = newMin;
	},
	{ immediate: true }
);

watch(
	max,
	newMax => {
		localMax.value = newMax;
	},
	{ immediate: true }
);
</script>

<template>
	<div :class="cn('flex w-full flex-col gap-5', props.class)">
		<h4 :class="intervalInputTitleVariants({ variant: props.variant })">
			{{ title }}
		</h4>
		<form class="flex w-full items-center gap-2">
			<CurrencyInput
				:id="firstInputId"
				v-model="localMin"
				:placeholder="minLabel"
				:error="error"
				:affix="unit"
				:unit-position="unitPosition"
				:unit="unit"
				tabindex="0"
				class="flex-1"
				:class="
					cn(
						intervalInputClassesVariants({ variant: props.variant }),
						props.inputClasses
					)
				"
				:affix-class="intervalInputSuffixVariants({ variant: props.variant })"
				:label-class="intervalInputLabelVariants({ variant: props.variant })"
				@change="handleInput('min')"
				@blur="handleBlur"
			/>
			<IconArrowLeftRight
				:class="intervalInputIconVariants({ variant: props.variant })"
			/>
			<CurrencyInput
				:id="secondInputId"
				v-model="localMax"
				:placeholder="maxLabel"
				:error="error"
				:affix="unit"
				:unit-position="unitPosition"
				:unit="unit"
				tabindex="0"
				class="flex-1"
				:class="
					cn(
						intervalInputClassesVariants({ variant: props.variant }),
						props.inputClasses
					)
				"
				:affix-class="intervalInputSuffixVariants({ variant: props.variant })"
				:label-class="intervalInputLabelVariants({ variant: props.variant })"
				@change="handleInput('max')"
				@blur="handleBlur"
			/>
		</form>
	</div>
</template>
